import React, { useState } from 'react'
import { Button, Dropdown, Menu, Icon, Input, Tooltip } from 'antd'
import SortableShotList from './ShortableShotList'
import VoiceoverModal from './/VoiceoverModal'
import SceneDurationFeedback from './SceneDurationFeedback'

const { TextArea } = Input

const SceneContainer = ({
  scene,
  isLoading,
  formData,
  setFormData,
  updateSceneData,
  addShotToScene,
  handleDeleteScene,
  handleDeleteShot,
  handleRegenerateImage,
  handleRegenerateWithReference,
  handleRegenerateWithReferenceNanoBanana,
  handleEditSettings,
  updateShotDataRef,
  handleSortEnd,
  generateVoiceForScene,
  loadingVoices,
  voices = [],
  onRefreshVoices,
  isClusteringAudio,
  audioClusteringProgress,
  audioClusteringStatus,
  handleFileUpload,
  isUploadingImage,
  isFetchingCharacters,
  videoId,
  onStopGeneration,
  ...props
}) => {
  const [voiceModalVisible, setVoiceModalVisible] = useState(false)

  const handleOpenVoiceModal = () => {
    setVoiceModalVisible(true)
  }

  const handleCloseVoiceModal = () => {
    setVoiceModalVisible(false)
  }

  const handleVoiceGenerate = async payload => {
    const sceneId = scene.id

    // Update scene with generating state - only update text for aggregated_voiceover
    // We rely on VoiceoverModal's local state for loading feedback now
    updateSceneData(sceneId, 'aggregated_voiceover', payload.text)

    try {
      const outputUrl = await generateVoiceForScene(sceneId, payload)
      // Success update handled in useVoiceOver hook (updating scene.audio_link)
      // We can just return the url here
      return outputUrl
    } catch (err) {
      // Error logging handled in hook or console
      throw err
    }
  }

  const isDialogueMode = formData.audioMode === 'dialogue'

  return (
    <div className="scene-container">
      <div className="left-panel">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ margin: 0 }}>{scene.title}</h2>
            <SceneDurationFeedback scene={scene} />

            {!isDialogueMode && (
              <Tooltip
                title={
                  isClusteringAudio
                    ? 'Audio Generation in progress...'
                    : 'Add Audio'
                }
              >
                <Button
                  type="primary"
                  ghost
                  size="small"
                  onClick={handleOpenVoiceModal}
                  // disabled={isClusteringAudio}
                  style={{
                    height: 32,
                    marginLeft: 4,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  Audio <Icon type={isClusteringAudio ? 'loading' : 'sound'} />
                </Button>
              </Tooltip>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="add-shot"
                    onClick={() => addShotToScene(scene.id)}
                  >
                    <Icon type="plus" /> Add Shot
                  </Menu.Item>
                  <Menu.Item
                    key="delete-scene"
                    onClick={() => handleDeleteScene(scene.id)}
                  >
                    <Icon type="delete" /> Delete Scene
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
              placement="bottomRight"
            >
              <Button type="link" size="small">
                Action <Icon type="down" />
              </Button>
            </Dropdown>
          </div>
        </div>
        <TextArea
          value={scene.description}
          rows={2}
          readOnly={isLoading}
          onChange={e => {
            const newDescription = e.target.value
            updateSceneData(scene.id, 'description', newDescription)
          }}
        />
      </div>
      <div className="right-panel">
        <SortableShotList
          shots={scene.shots}
          sceneId={scene.id}
          formData={formData}
          setFormData={setFormData}
          handleDeleteShot={handleDeleteShot}
          handleRegenerateImage={handleRegenerateImage}
          handleRegenerateWithReference={handleRegenerateWithReference}
          handleRegenerateWithReferenceNanoBanana={
            handleRegenerateWithReferenceNanoBanana
          }
          handleEditSettings={handleEditSettings}
          updateShotDataRef={updateShotDataRef}
          onSortEnd={params => handleSortEnd(scene.id, params)}
          axis="x"
          useDragHandle
          distance={1}
          helperClass="sortable-helper"
          lockAxis="x"
          lockToContainerEdges
          handleFileUpload={handleFileUpload}
          isUploadingImage={isUploadingImage}
          videoId={videoId}
          isFetchingCharacters={isFetchingCharacters}
        />
      </div>

      <VoiceoverModal
        visible={voiceModalVisible}
        isClusteringAudio={isClusteringAudio}
        scene={scene}
        voices={voices}
        videoId={videoId}
        loadingVoices={loadingVoices}
        formData={formData}
        selectedVoice={formData?.selectedVoice}
        characterMap={formData?.characterMap}
        characterVoices={formData?.characterVoices}
        onClose={handleCloseVoiceModal}
        onGenerate={handleVoiceGenerate}
        onRefreshVoices={onRefreshVoices}
        onNavigateToStep={props.onNavigateToStep}
        onSceneStructureChange={props.onSceneStructureChange}
        audioClusteringProgress={audioClusteringProgress}
        audioClusteringStatus={audioClusteringStatus}
        audioMode={formData.audioMode}
        onStopGeneration={onStopGeneration}
      />
    </div>
  )
}

export default SceneContainer
